import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Home, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import { useCurrency } from "@/hooks/useCurrency";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { listPriceLabel, apiError } from "@/pages/masters/menuItemFormUtils";
import { itemHasSizes } from "@/lib/menuItemSizes";
import "@/styles/simple-ui.css";

const PER_PAGE = 30;
const ALL = "all";

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function SimpleProductsList() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState(ALL);
  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId]);

  const { data: mainMenus = [] } = useQuery({
    queryKey: ["main-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/main-menus", { params: { branch_id: branchId } });
      return data.main_menus ?? [];
    },
    enabled: Boolean(branchId),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["menu-items", branchId, page, debouncedSearch, categoryId, "simple"],
    queryFn: async () => {
      const { data: res } = await api.get("/menu-items", {
        params: {
          branch_id: branchId,
          page,
          per_page: PER_PAGE,
          search: debouncedSearch || undefined,
          main_menu_id: categoryId !== ALL ? categoryId : undefined,
        },
      });
      return res;
    },
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
  });

  const items = data?.menu_items ?? [];
  const meta = data?.meta ?? {};
  const total = meta.total ?? items.length;
  const lastPage = meta.last_page || 1;

  const deleteMutation = useMutation({
    mutationFn: async (itemId) =>
      api.delete(`/menu-items/${itemId}`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", branchId] });
      toast.success(res.data.message || "Deleted");
    },
    onError: (err) => toast.error(apiError(err, "Failed to delete")),
  });

  const categoryOptions = useMemo(
    () => [{ id: ALL, name: t("simple.posAll", "All") }, ...mainMenus.map((m) => ({ id: String(m.id), name: m.name }))],
    [mainMenus, t],
  );

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-products-list">
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <Link to="/masters" className="inline-flex h-10 items-center gap-1 rounded-xl bg-slate-100 px-3 text-sm font-bold text-slate-700">
          <ArrowLeft className="h-4 w-4" />
          {t("nav.masters")}
        </Link>
        <h1 className="flex-1 text-lg font-extrabold text-slate-900">{t("nav.menuItem")}</h1>
        <button
          type="button"
          onClick={() => navigate("/masters/menu-item/new")}
          className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-emerald-600 px-4 text-sm font-extrabold text-white"
          data-testid="menu-item-add"
        >
          <Plus className="h-4 w-4" />
          {t("simple.mastersAdd", "Add")}
        </button>
      </div>

      <div className="space-y-2 border-b bg-white p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search")}
            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 ps-9 pe-3 text-[15px] outline-none focus:border-violet-400"
            data-testid="menu-item-search"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {categoryOptions.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategoryId(c.id)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                categoryId === c.id ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
              )}
            >
              {c.name}
            </button>
          ))}
          {isFetching && !isLoading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" /> : null}
        </div>
        <div className="text-xs font-semibold text-slate-400">{total} {t("simple.mastersItems", "items")}</div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">{t("simple.mastersNoProducts", "No products yet")}</div>
        ) : (
          <ul className="mx-auto max-w-3xl space-y-2">
            {items.map((item) => {
              const img = resolveMediaUrl(item.img || item.image_url);
              return (
                <li key={item.id}>
                  <div className="flex items-stretch gap-2 rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 text-start"
                      onClick={() => navigate(`/masters/menu-item/${item.id}/edit`)}
                    >
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                        {img ? (
                          <img src={img} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="grid h-full place-items-center text-xs font-bold text-slate-400">—</div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[16px] font-extrabold text-slate-900">{item.name}</div>
                        <div className="truncate text-xs text-slate-500">
                          {item.code} · {item.main_menu || "—"} / {item.sub_menu || "—"}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-black text-violet-700">{listPriceLabel(item, format)}</span>
                          {itemHasSizes(item) ? (
                            <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">S/M/L</span>
                          ) : null}
                          <span
                            className={cn(
                              "rounded-md px-1.5 py-0.5 text-[10px] font-bold",
                              item.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500",
                            )}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      className="grid w-12 shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600"
                      onClick={() => {
                        if (window.confirm(`Delete "${item.name}"?`)) deleteMutation.mutate(item.id);
                      }}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {lastPage > 1 ? (
        <div className="flex items-center justify-center gap-3 border-t bg-white py-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-10 rounded-xl bg-slate-100 px-4 text-sm font-bold disabled:opacity-40"
          >
            ←
          </button>
          <span className="text-sm font-bold text-slate-600">{page} / {lastPage}</span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="h-10 rounded-xl bg-slate-100 px-4 text-sm font-bold disabled:opacity-40"
          >
            →
          </button>
        </div>
      ) : null}
    </div>
  );
}
